/**
 * @fileoverview Input/output schemas for the world-element-chunked-extractor-v3 agent.
 *
 * This agent is designed for processing large documents in chunks. It takes:
 * - A chunk of text to analyze
 * - Previously extracted world elements (partialElements)
 * - Chunk metadata for context
 *
 * It combines detection (surface + inferential) and enrichment (descriptions)
 * in a single pass, outputting a merged list of enriched world elements.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { ChunkInfoSchema } from '@/lib/content-chunking';
import {
  ElementClassificationEnum,
  WorldElementSubtypeEnum,
} from '@/domains/world-building/schemas/world-element-detection';
import { WorldElementTypeV3Enum } from '@/domains/world-building/schemas';

// =============================================================================
// ENRICHED ELEMENT SCHEMA (same shape as detailer output)
// =============================================================================

/**
 * An enriched world element with detection fields + initialDescription.
 * Same shape as the detailer's output schema.
 */
export const EnrichedWorldElementSchema = z.object({
  name: z.string().describe('The element name as it appears in the text, using a consistent canonical form.'),
  type: WorldElementTypeV3Enum.describe('The world element type.'),
  element: WorldElementSubtypeEnum.describe('The element sub-type (must be a specific subtype, never a parent type name).'),
  classification: ElementClassificationEnum,
  initialDescription: z.string().describe(
    'Description of the element based on the text. ' +
    'Length varies by classification: critical = 3-5 sentences, ' +
    'major = 2-3 sentences, minor = 1 sentence.'
  ),
});

export type EnrichedWorldElement = z.infer<typeof EnrichedWorldElementSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /**
   * The current chunk of text to analyze.
   * May include overlap from adjacent chunks for context continuity.
   */
  textChunk: z.string().describe('The current chunk of text to analyze for world elements.'),

  /**
   * Elements extracted from previous chunks.
   * Empty array for the first chunk.
   * The agent should merge new findings with this list.
   */
  partialElements: z.array(EnrichedWorldElementSchema).describe(
    'World elements extracted from previous chunks. Empty for first chunk. Agent should merge new findings with this list.'
  ),

  /**
   * Metadata about this chunk's position in the document.
   * Helps the agent understand context (first chunk, middle, last).
   */
  chunkInfo: ChunkInfoSchema.describe('Metadata about this chunk position in the full document.'),

  /**
   * Genre-informed priority guidance for element types, pre-formatted as readable text.
   */
  genrePriorities: z.string().optional().describe(
    'Genre-informed priority guidance for element types. Formatted as readable text.'
  ),

  /**
   * Names of already-extracted elements to avoid duplicates.
   */
  existingElements: z.string().optional().describe(
    'Names of already-existing world elements to skip during detection.'
  ),
}).merge(AGENT_SCHEMA_OBJECTS.loreContext.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Summary of what changed in this chunk (useful for debugging and UI feedback).
 */
export const ChunkProcessingSummarySchema = z.object({
  /** Number of new elements discovered in this chunk */
  newCount: z.number().min(0).describe('Number of new world elements discovered in this chunk (integer >= 0).'),

  /** Number of existing elements that were updated with new information */
  amendedCount: z.number().min(0).describe('Number of existing elements amended with new information (integer >= 0).'),

  /** Number of elements that remained unchanged */
  unchangedCount: z.number().min(0).describe('Number of elements that remained unchanged (integer >= 0).'),
}).describe('Summary of world element extraction results for this chunk.');

export type ChunkProcessingSummary = z.infer<typeof ChunkProcessingSummarySchema>;

export const OutputSchema = z.object({
  /**
   * Complete merged list of all world elements.
   * Includes both previously extracted elements (potentially amended)
   * and newly discovered elements from this chunk.
   */
  elements: z.array(EnrichedWorldElementSchema).describe(
    'Complete merged list of all world elements including previous and newly discovered.'
  ),

  /**
   * Optional summary of what changed in this chunk.
   * Useful for progress tracking and debugging.
   */
  summary: ChunkProcessingSummarySchema.optional().describe(
    'Optional summary of extraction results for this chunk.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
